import Vue from 'vue'
import {
  loginUser,
  getUserInfo,
  searchUsers,
  sendRecoverPasswordMail,
  sendQuotation,
  getTermsAndConditions,
  signTermsAndConditions,
  setLanguageConfiguration,
  setNavigationTutorialStatus,
  externalGoogleLogin,
  externalMicrosoftLogin,
  getExternalGoogleInfo,
  getExternalMicrosoftInfo,
  getUserCustomFields
} from '../../services/UserService'
import successIcon from '../../assets/il_mail_newpassword.svg'
import { isError, isNotFound } from '../../utils/api'
import router, { routes } from '../../router'
import { i18n } from '../../main'
import { getSchoolsWithCourses } from '../../services/SchoolGroupService'
import { searchAuthors } from '../../services/KreaEducationalContent'

export default {
  state: {
    userLoggedIn: false,
    userInfo: null,
    customFields: null,
    schoolsWithCourses: null,
    googleUserInfo: null,
    microsoftUserInfo: null,
    searchedUsers: [],
    searchedAuthors: [],
    showInvalidUserError: false,
    termsAndConditions: null
  },

  getters: {
    isAdminUser: state => {
      const { primaryRoles } = state.userInfo
      return primaryRoles && !!primaryRoles.find(rol => rol.name === 'Admin')
    }
  },

  mutations: {
    loginUser(state) {
      state.userLoggedIn = true
    },
    logoutUser(state) {
      state.userLoggedIn = false
    },
    setUserInfo(state, info) {
      state.userInfo = info
    },
    setCustomFields(state, data) {
      state.customFields = data
    },
    setSearchedUsers(state, users) {
      state.searchedUsers = users
    },
    setAvailableAuthors(state, authors) {
      state.searchedAuthors = authors
    },
    setShowInvalidUserError(state, info) {
      state.showInvalidUserError = info
    },
    setTermsAndConditions(state, url) {
      state.termsAndConditions = url
    },
    editConfigurationLanguage(state, { field, value }) {
      state.userInfo.languageConfiguration[field] = value
    },
    setGoogleUserInfo(state, data) {
      state.googleUserInfo = data
    },
    setMicrosoftUserInfo(state, data) {
      state.microsoftUserInfo = data
    },
    setSchoolsWithCourses(state, data) {
      state.schoolsWithCourses = data
    },
    clearGoogleUserInfo(state) {
      state.googleUserInfo = null
    },
    clearMicrosoftUserInfo(state) {
      state.microsoftUserInfo = null
    }
  },

  actions: {
    setUserError({ commit }, errorState) {
      commit('setShowInvalidUserError', errorState)
    },
    async login({ commit }, { email, password, subdomain }) {
      commit('show', { element: 'fullPageLoader' })
      try {
        await loginUser(email, password, subdomain)
        commit('setShowInvalidUserError', false)
        commit('loginUser')
      } catch (error) {
        commit('setShowInvalidUserError', true)
      }
      commit('hide', { element: 'fullPageLoader' })
    },
    async getLoggedUserInfo({ commit }) {
      const response = await getUserInfo().catch(e => e.response)
      const success = !isError(response.status)
      if (success) {
        commit('setUserInfo', response.data)
      } else {
        commit('logoutUser')
      }
    },
    async getUserCustomFields({ commit }) {
      const response = await getUserCustomFields().catch(e => e.response)
      const success = !isError(response.status)
      if (success) commit('setCustomFields', response.data)
      return { success, data: response.data }
    },
    userIsLoggedIn({ commit }) {
      commit('loginUser')
    },
    logout({ commit }) {
      commit('hide', { element: 'fullPageLoader' })
      commit('clearHighlightNotification')
      commit('logoutUser')
    },
    clearGoogleUserInfo({ commit }) {
      commit('clearGoogleUserInfo')
    },
    clearMicrosoftUserInfo({ commit }) {
      commit('clearMicrosoftUserInfo')
    },
    async searchUsers({ commit }, filters = {}) {
      const response = await searchUsers(filters).catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) commit('setSearchedUsers', response.data)
      return successfulRequest
    },
    async searchAuthors({ commit }, filters = {}) {
      const response = await searchAuthors(filters).catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) commit('setAvailableAuthors', response.data)
      return successfulRequest
    },
    async requestPasswordChange({ commit }, { mail, successModalProps }) {
      const response = await sendRecoverPasswordMail(mail).catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) {
        commit('show', { element: 'successModal', props: successModalProps })
        commit('hide', { element: 'forgotPasswordModal' })
      } else return response.status
    },
    async sendQuotation({ commit }, params) {
      const response = await sendQuotation(params).catch(e => e.response)
      if (!isError(response.status)) {
        commit('show', {
          element: 'successModal',
          props: {
            title: i18n.t('quote.thanksInterest'),
            subtitle: i18n.t('quote.weWillContact'),
            icon: successIcon,
            showCloseButton: true,
            scopeStyle: 'quote',
            onClose: () => router.push({ name: routes.feed })
          }
        })
      } else {
        Vue.notify({ group: 'custom-toast', duration: 5000 })
      }
    },
    async getTermsAndConditions({ commit }) {
      const response = await getTermsAndConditions().catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) commit('setTermsAndConditions', response.data.termsAndConditionsURL)
      return successfulRequest
    },
    async signTermsAndConditions({ commit }) {
      const response = await signTermsAndConditions().catch(e => e.response)
      const successfulRequest = !isError(response.status)
      return successfulRequest
    },
    async setLanguageConfiguration({ commit, dispatch }, { mailingLanguage, navigationLanguage }) {
      const response = await setLanguageConfiguration({ mailingLanguage, navigationLanguage }).catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) {
        commit('setUserInfo', response.data)
        dispatch('changeLocale', navigationLanguage)
      }
      return successfulRequest
    },
    editConfigurationLanguage({ commit }, { field, value }) {
      commit('editConfigurationLanguage', { field, value })
    },
    async setNavigationTutorialStatus({ commit }, status) {
      const response = await setNavigationTutorialStatus(status).catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) commit('setUserInfo', response.data)
      return successfulRequest
    },
    async externalGoogleLogin({ commit, dispatch }, googleToken) {
      const response = await externalGoogleLogin(googleToken).catch(e => e.response)
      const success = !isError(response.status)
      const notFound = isNotFound(response.status)
      if (success) {
        commit('setShowInvalidUserError', false)
        commit('loginUser')
      }
      if (notFound) {
        await dispatch('getExternalGoogleInfo', googleToken)
        router.push({ name: routes.trial })
      }
      return success
    },
    async externalMicrosoftLogin({ commit, dispatch }, microsoftToken) {
      const response = await externalMicrosoftLogin(microsoftToken).catch(e => e.response)
      const success = !isError(response.status)
      const notFound = isNotFound(response.status)
      if (success) {
        commit('setShowInvalidUserError', false)
        commit('loginUser')
      }
      if (notFound) {
        await dispatch('getExternalMicrosoftInfo', microsoftToken)
        router.push({ name: routes.trial })
      }
      return success
    },
    async getExternalGoogleInfo({ commit }, googleToken) {
      const response = await getExternalGoogleInfo(googleToken).catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) commit('setGoogleUserInfo', response.data)
      return successfulRequest
    },
    async getExternalMicrosoftInfo({ commit }, microsoftToken) {
      const response = await getExternalMicrosoftInfo(microsoftToken).catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) commit('setMicrosoftUserInfo', response.data)
      return successfulRequest
    },
    async hydrateSchoolsWithCourses({ commit, state }) {
      const response = await getSchoolsWithCourses(state.userInfo.schoolGroupId).catch(e => e.response)
      const successfulRequest = !isError(response.status)
      if (successfulRequest) commit('setSchoolsWithCourses', response.data)
      return successfulRequest
    }
  }
}
